import { useState } from "react";
import { t, font, Button, Input } from "@realtyflows/ui";
import { lookupMlsListing, autoFillFolderFromMls } from "../api";

/**
 * MlsLookup — V4 Features 4.1 + 4.2
 *
 * Pops on FolderDetail. Type an MLS#, see a preview, click "Apply" to
 * auto-fill every form in the folder with property data.
 *
 * Props:
 *   folderId    — the folder to auto-fill into
 *   onApplied() — called after a successful auto-fill so the parent can refresh
 */
export default function MlsLookup({ folderId, onApplied }) {
  const [mlsNumber, setMlsNumber] = useState("");
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const fmtMoney = (cents) => cents != null ? `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—";

  const handleLookup = async () => {
    if (!mlsNumber.trim()) return;
    setLoading(true);
    setError("");
    setListing(null);
    setResult(null);
    try {
      const data = await lookupMlsListing(mlsNumber.trim());
      setListing(data);
    } catch (err) {
      setError(err.message || "MLS lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setError("");
    try {
      const r = await autoFillFolderFromMls(folderId, mlsNumber.trim(), overwrite);
      setResult(r);
      if (onApplied) onApplied(r);
    } catch (err) {
      setError(err.message || "Auto-fill failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Input
          placeholder="Enter MLS# (e.g. 11234567)"
          value={mlsNumber}
          onChange={(e) => setMlsNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
        />
        <Button variant="primary" onClick={handleLookup} disabled={loading || !mlsNumber.trim()}>
          {loading ? "Looking up..." : "Look up"}
        </Button>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: t.coral, marginBottom: 10, fontFamily: font.body }}>{error}</div>
      )}

      {listing && (
        <div style={{ padding: 14, background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
                {listing.propertyAddress}
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>
                {listing.city}, {listing.state} {listing.zip} · MLS #{listing.mlsNumber}
              </div>
            </div>
            <span style={{
              padding: "3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 700, fontFamily: font.mono,
              background: listing.status === "active" ? t.emerald + "15" : t.amber + "15",
              color: listing.status === "active" ? t.emerald : t.amber,
              border: `1px solid ${listing.status === "active" ? t.emerald : t.amber}30`,
            }}>
              {listing.status.toUpperCase()}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
            <Stat label="Price" value={fmtMoney(listing.listPrice)} />
            <Stat label="Beds / Baths" value={`${listing.beds} / ${listing.baths}`} />
            <Stat label="Sq Ft" value={(listing.squareFeet || 0).toLocaleString()} />
            <Stat label="Year Built" value={listing.yearBuilt || "—"} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: t.textMuted, fontFamily: font.body, cursor: "pointer" }}>
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
              Overwrite existing field values
            </label>
            <Button variant="primary" onClick={handleApply} disabled={applying}>
              {applying ? "Filling..." : "Auto-fill folder forms"}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div style={{
          padding: 10, background: t.emerald + "10", border: `1px solid ${t.emerald}30`,
          borderRadius: 8, fontSize: 12, color: t.textPrimary, fontFamily: font.body,
        }}>
          ✓ Filled <strong>{result.fieldsFilled}</strong> field{result.fieldsFilled === 1 ? "" : "s"} across <strong>{result.formsUpdated}</strong> form{result.formsUpdated === 1 ? "" : "s"}.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: t.surface, borderRadius: 6, padding: "6px 10px" }}>
      <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.mono, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginTop: 2 }}>{value}</div>
    </div>
  );
}
